import {
    Component,
    forwardRef,
    Input,
    ViewChild,
    ElementRef,
    OnChanges,
    OnDestroy,
    OnInit
} from '@angular/core';

import {
    Location
} from '@angular/common';

import { 
    ControlValueAccessor, 
    NG_VALUE_ACCESSOR, 
    Validator, 
    NG_VALIDATORS 
} from '@angular/forms';

import { Editor } from 'tinymce';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { LocalizationService } from '../../services/localization.service';
import { PgHtmlMode } from '../../models/config.resources.model';


@Component({
    selector: 'app-pg-html-editor',
    templateUrl: './pg-html-editor.component.html',
    styleUrls: ['./pg-html-editor.component.scss'],
    providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PgHtmlEditorComponent),
            multi: true
        }, {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => PgHtmlEditorComponent),
            multi: true,
        }],
    standalone: false
})

export class PgHtmlEditorComponent implements ControlValueAccessor, Validator, OnInit, OnChanges, OnDestroy {

    constructor(private location:Location, private modalService:NgbModal, private localizationService:LocalizationService) {
    }

    @Input() fieldId:string;

    @Input() readonly: boolean;
    @Input() required: boolean;

    @Input() allowAdvanced:boolean;

    @Input() htmlMode:PgHtmlMode;

    @Input() options:any;

    showAdvanced = false;

    editor:Editor;
    value:string = null;

    isInitialized = false;

    editorSettingsByMode:{ [mode:string]: {
        toolbar: string,
        plugins:Array<string>,
        allowLists: boolean,
        allowHeadings: boolean,
    } } = {
        'inline-only': {
            toolbar: 'cut copy fixPaste | bold italic underline | link | undo redo',
            plugins: ['link'],
            allowLists: false,
            allowHeadings: false,
        },
        'rich': {
            toolbar: 'cut copy fixPaste | bold italic underline | link | h3 h4 h5 | bullist numlist | undo redo',
            plugins: ['link','lists'],
            allowLists: true,
            allowHeadings: true,
        },
        'full':{
            toolbar: 'cut copy fixPaste | bold italic underline | link | h3 h4 h5 | bullist numlist | entouranceImage | undo redo',
            plugins: ['link','lists'],
            allowLists: true,
            allowHeadings: true,
        }
    }

    editorParams = null;

    private _baseEditorParams = { 
        base_url: this.location.prepareExternalUrl('/assets/tinymce'), 
        suffix: '.min',
        plugins: null,
        menubar: false,
        statusbar: false,
        toolbar: null,
        paste_data_images: false,
        paste_webkit_styles: 'none',
        paste_preprocess: (editor, args) => {
            // TEMP: debug paste from Word/Docs/Mac — remove when done
            console.log('[pg-html-editor] paste raw:', args.content);

            args.content = this._sanitizePasteHtml(args.content as string);

            console.log('[pg-html-editor] paste clean:', args.content);
        },
        entity_encoding: 'raw',
        setup: (editor:Editor) => {
            editor.ui.registry.addButton('entouranceImage', {
                icon: 'image',
                tooltip: this.localizationService.translate('pg-html-editor.insert-image'),
                onAction: (_) =>  {  
                    this.selectImageOpen();
                }
            });

            editor.ui.registry.addButton('fixPaste', {
                icon: 'paste',
                tooltip: 'Paste',
                onAction: (_) =>  {  
                    navigator.clipboard.readText().then((text) => {
                        if(text != null && text != '') {
                            this.editor.insertContent(text);
                        }
                    })
                }
            });

            editor.on('init', () => {
                this.editor = editor;

                this.editor.readonly = this.readonly;

                this.editor.undoManager.clear();
            });
            
            editor.on('change', () => {
                this.value = editor.getContent();
                this.onValueChange()
            });

            editor['___remove'] = editor.remove
            editor.remove = () => {
                try {
                    editor['___remove']()
                }
                catch(ex) {
                    console.error(ex)
                }
            }
        }
    }

    private _handleChanges() {
        if(this.htmlMode == null) this.htmlMode = 'full'

        let _settings = this.editorSettingsByMode[this.htmlMode]

        this.editorParams = { ...this._baseEditorParams }

        this.editorParams.toolbar = _settings.toolbar
        this.editorParams.plugins = _settings.plugins

        if(this.options) {
            for(let i in this.options) {
                this.editorParams[i] = this.options[i]
            }
        }

        if(this.editor != null) {
            this.editor.readonly = this.readonly;
        }
    }

    /**
     * Normalizes paste HTML from Word / Google Docs / Pages / etc.
     * Keeps semantic formatting supported by the current htmlMode, maps near-equivalent
     * tags, flattens tables to paragraphs, and unwraps the rest without losing text.
     */
    private _sanitizePasteHtml(content: string): string {
        if (content == null || content === '') return '';

        const mode = this.htmlMode ?? 'full';
        const settings = this.editorSettingsByMode[mode] ?? this.editorSettingsByMode['full'];
        const options = {
            allowLists: settings.allowLists,
            allowHeadings: settings.allowHeadings,
        };

        // Comments (incl. Word conditionals) + Word/XML namespace noise.
        let html = content
            .replace(/<!--[\s\S]*?-->/g, '')
            .replace(/<\/?(?:o|w|m):[^>]*>/gi, '')
            .replace(/<xml[\s\S]*?<\/xml>/gi, '')
            .replace(/<style[\s\S]*?<\/style>/gi, '')
            .replace(/<script[\s\S]*?<\/script>/gi, '');

        const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');

        doc.body.querySelectorAll('script, style, meta, link, noscript, title').forEach((el) => el.remove());

        // Flatten tables before the recursive clean so cell content is still sanitized.
        Array.from(doc.body.querySelectorAll('table')).forEach((table) => {
            const rows = Array.from(table.querySelectorAll('tr')).map((tr) => {
                const p = doc.createElement('p');
                const cells = Array.from(tr.querySelectorAll('th, td'));
                cells.forEach((cell, index) => {
                    if (index > 0) p.appendChild(doc.createTextNode(' '));
                    while (cell.firstChild) p.appendChild(cell.firstChild);
                });
                return p;
            });
            if (rows.length === 0) {
                table.remove();
            }
            else {
                table.replaceWith(...rows);
            }
        });

        const cleaned = this._cleanPasteNode(doc.body, doc, options);
        let result = cleaned.innerHTML;

        result = result
            .replace(/(?:<br\s*\/?>\s*){3,}/gi, '<br><br>')
            .replace(/[ \t]{2,}/g, ' ')
            .replace(/((?:&nbsp;)|\s)+$/g, '')
            .trim();

        return result;
    }

    private _cleanPasteNode(
        source: Node,
        doc: Document,
        options: { allowLists: boolean, allowHeadings: boolean },
    ): HTMLElement {
        const container = doc.createElement('div');

        Array.from(source.childNodes).forEach((child) => {
            if (child.nodeType === Node.TEXT_NODE) {
                const text = child.textContent ?? '';
                if (text.length) container.appendChild(doc.createTextNode(text));
                return;
            }

            if (child.nodeType !== Node.ELEMENT_NODE) return;

            const el = child as HTMLElement;
            const tag = el.tagName.toLowerCase();
            const cleanedChildren = this._cleanPasteNode(el, doc, options);
            const hasContent = this._pasteNodeHasContent(cleanedChildren);

            const appendUnwrapped = () => {
                while (cleanedChildren.firstChild) container.appendChild(cleanedChildren.firstChild);
            };

            const appendWrapped = (wrapperTag: string) => {
                if (!hasContent) return;
                container.appendChild(this._wrapPasteChildren(doc, wrapperTag, cleanedChildren));
            };

            switch (tag) {
                case 'b':
                case 'strong':
                    appendWrapped('strong');
                    break;
                case 'i':
                case 'em':
                    appendWrapped('em');
                    break;
                case 'u':
                    appendWrapped('u');
                    break;
                case 'br':
                case 'hr':
                    container.appendChild(doc.createElement('br'));
                    break;
                case 'p':
                    appendWrapped('p');
                    break;
                case 'div':
                case 'section':
                case 'article':
                case 'header':
                case 'footer':
                case 'aside':
                case 'main':
                case 'blockquote':
                case 'pre':
                case 'figure':
                case 'figcaption':
                    if (this._pasteHasBlockChild(cleanedChildren)) {
                        appendUnwrapped();
                    }
                    else {
                        appendWrapped('p');
                    }
                    break;
                case 'h1':
                case 'h2':
                    appendWrapped(options.allowHeadings ? 'h3' : 'p');
                    break;
                case 'h3':
                case 'h4':
                case 'h5':
                    appendWrapped(options.allowHeadings ? tag : 'p');
                    break;
                case 'h6':
                    appendWrapped(options.allowHeadings ? 'h5' : 'p');
                    break;
                case 'ul':
                case 'ol':
                    if (options.allowLists) {
                        appendWrapped(tag);
                    }
                    else {
                        Array.from(cleanedChildren.childNodes).forEach((liChild) => {
                            if (liChild.nodeType === Node.ELEMENT_NODE && (liChild as HTMLElement).tagName.toLowerCase() === 'li') {
                                const p = doc.createElement('p');
                                while (liChild.firstChild) p.appendChild(liChild.firstChild);
                                if (this._pasteNodeHasContent(p)) container.appendChild(p);
                            }
                            else {
                                container.appendChild(liChild);
                            }
                        });
                    }
                    break;
                case 'li':
                    if (options.allowLists) {
                        appendWrapped('li');
                    }
                    else {
                        appendWrapped('p');
                    }
                    break;
                case 'a': {
                    const href = (el.getAttribute('href') || '').trim();
                    if (hasContent && href && /^(https?:\/\/|mailto:|\/|#)/i.test(href)) {
                        const a = doc.createElement('a');
                        a.setAttribute('href', href);
                        while (cleanedChildren.firstChild) a.appendChild(cleanedChildren.firstChild);
                        container.appendChild(a);
                    }
                    else {
                        appendUnwrapped();
                    }
                    break;
                }
                // Inline wrappers / Word-Docs noise: keep children only.
                case 'span':
                case 'font':
                case 'label':
                case 's':
                case 'strike':
                case 'del':
                case 'ins':
                case 'mark':
                case 'small':
                case 'big':
                case 'center':
                case 'tbody':
                case 'thead':
                case 'tfoot':
                case 'colgroup':
                case 'col':
                    appendUnwrapped();
                    break;
                default:
                    appendUnwrapped();
                    break;
            }
        });

        return container;
    }

    private _wrapPasteChildren(doc: Document, tag: string, from: HTMLElement): HTMLElement {
        const el = doc.createElement(tag);
        while (from.firstChild) el.appendChild(from.firstChild);
        return el;
    }

    private _pasteNodeHasContent(node: HTMLElement): boolean {
        if ((node.textContent || '').replace(/\u00a0/g, ' ').trim().length > 0) return true;
        return !!node.querySelector('br, img, a');
    }

    private _pasteHasBlockChild(node: HTMLElement): boolean {
        return Array.from(node.children).some((child) => {
            const tag = child.tagName.toLowerCase();
            return ['p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'table', 'blockquote', 'pre', 'section', 'article'].indexOf(tag) !== -1;
        });
    }

    onValueChange() {
        console.log('change', this.value)
        if(this._onTouched != null) this._onTouched();
        if(this._onChange != null) this._onChange(this.value);
    }

    ngOnChanges() {
        this.isInitialized = true;
        this._handleChanges();
    }

    ngOnInit(): void {
        if(!this.isInitialized) {
            this.isInitialized = true;
            this._handleChanges();
        }
    }

    ngOnDestroy(): void {
    }

    setEditorValue() {
        if(this.editor != null) {
            const newContent = this.value == null ? '' : this.value;

            if(this.editor.getContent({ format: 'raw' }) !== newContent) {
                this.editor.setContent(newContent, { format: 'raw', no_selection: true });
            }
        }
    }

    @ViewChild('modalSelectImage') modalSelectImage:ElementRef;

    selectImageValue:string = null;

    private _selectImageModalRef:NgbModalRef = null;

    selectImageOpen() {
        this.selectImageValue = null;
        this._selectImageModalRef = this.modalService.open(this.modalSelectImage);
        this._selectImageModalRef.result.then(() => {
            if(this.selectImageValue != null && this.selectImageValue != '') {
                this.editor.insertContent('<figure><img style="object-fit: cover; height: 100%; width: 100%;" src="' + this.selectImageValue + '"><figcaption>' + this.localizationService.translate('pg-html-editor.image-caption') + '</figcaption></figure>')
            }
        }, () => {})
    }

    selectImageDismiss() {
        this._selectImageModalRef.dismiss();
    }

    selectImageClose() {
        this._selectImageModalRef.close();
    }

    // INTERFACCIA ControlValueAccessor

    writeValue(obj: any) {
        this.value = obj;

        this.setEditorValue();
    }

    _onChange;

    registerOnChange(fn: any) {
        this._onChange = fn;
    }

    _onTouched;

    registerOnTouched(fn: any) {
        this._onTouched = fn;
    }

    // INTERFACCIA Validator

    validate() {
        return (!this.required || (this.value != '' && this.value != null)) ? null : {
            required: {
                valid: false
            }
        }
    };
}
  